/**
 * 合同风险扫描引擎
 * 基于规则关键词库（内置 + 自定义），扫描合同文本中的潜在风险条款
 */

import { getAllRiskRules } from './riskRules';
import type { RiskLevel } from './riskRules';

// 风险条目
export interface RiskItem {
  id: string;
  level: RiskLevel;
  title: string;         // 风险标题（简短）
  description: string;   // 风险说明
  suggestion: string;    // 修改建议
  keyword: string;       // 命中的关键词
  context: string;       // 命中上下文（前后各20字）
  position: number;      // 在全文中的字符位置
  legalBasis?: string;   // 法律依据（新增）
}

// 扫描结果
export interface RiskScanResult {
  total: number;
  highCount: number;
  mediumCount: number;
  lowCount: number;
  items: RiskItem[];
}

/**
 * 扫描合同文本，返回风险分析结果
 * @param text 合同全文（纯文本）
 */
export function scanContractText(text: string): RiskScanResult {
  const items: RiskItem[] = [];
  const allRules = getAllRiskRules();

  for (const rule of allRules) {
    const keyword = rule.keyword;
    const regex = new RegExp(escapeRegExp(keyword), 'gi');
    let match: RegExpExecArray | null;

    while ((match = regex.exec(text)) !== null) {
      const start = Math.max(0, match.index - 20);
      const end = Math.min(text.length, match.index + keyword.length + 20);
      const context = text.substring(start, end).replace(/\n/g, ' ');

      items.push({
        id: `risk-${items.length + 1}`,
        level: rule.level,
        title: rule.keyword,
        description: rule.description,
        suggestion: '',
        keyword,
        context: context.trim(),
        position: match.index,
        legalBasis: rule.legalBasis || undefined,
      });
    }
  }

  // 按位置排序，去重（同一位置同一关键词只保留一条）
  items.sort((a, b) => a.position - b.position);
  const seen = new Set<string>();
  const uniqueItems: RiskItem[] = [];
  for (const item of items) {
    const key = `${item.position}-${item.keyword}`;
    if (!seen.has(key)) {
      seen.add(key);
      uniqueItems.push(item);
    }
  }

  return {
    total: uniqueItems.length,
    highCount: uniqueItems.filter(i => i.level === 'high').length,
    mediumCount: uniqueItems.filter(i => i.level === 'medium').length,
    lowCount: uniqueItems.filter(i => i.level === 'low').length,
    items: uniqueItems,
  };
}

/**
 * 从 docx Blob 中提取纯文本并扫描风险
 * @param blob 生成的合同 docx Blob
 */
export async function scanContractBlob(blob: Blob): Promise<RiskScanResult> {
  try {
    const arrayBuffer = await blob.arrayBuffer();

    // 动态导入 mammoth（避免打包体积增加）
    const mammoth = await import('mammoth');
    const result = await mammoth.extractRawText({ arrayBuffer: arrayBuffer });
    const text = result.value; // 纯文本

    return scanContractText(text);
  } catch (err) {
    console.error('风险扫描失败:', err);
    return {
      total: 0,
      highCount: 0,
      mediumCount: 0,
      lowCount: 0,
      items: [],
    };
  }
}

function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
